import { useState, useEffect, useCallback } from 'react';
import { useAppLock } from '@/context/AppLockContext';
import { useAuth } from '@/context/AuthContext';
import { useBiometricAuth } from '@/hooks/security/useBiometricAuth';
import { usePinAuth } from '@/hooks/security/usePinAuth';
import { PinInput } from './PinInput';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/ui/use-toast';
import { signOut } from '@/lib/auth/sign-out';
import { useNavigate } from 'react-router-dom';
import { Lock, Fingerprint, LogOut, KeyRound } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';

const MAX_PIN_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 30 * 1000; // 30 seconds

export function AppLockScreen() {
  const { isLocked, unlock, hasPin } = useAppLock();
  const { user } = useAuth();
  const { credentials, settings, biometricUnlock, hasPlatformAuth } = useBiometricAuth();
  const { verifyPin } = usePinAuth();
  const navigate = useNavigate();

  const [pinError, setPinError] = useState<string | undefined>();
  const [isVerifying, setIsVerifying] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [biometricAttempted, setBiometricAttempted] = useState(false);

  // Simplified: if user has passkey and platform auth, allow biometric unlock
  // No separate setting needed - if lock on resume is enabled and passkey exists, use it
  const canUseBiometric =
    hasPlatformAuth &&
    credentials.length > 0;

  // Auto-trigger biometric on mount if available
  useEffect(() => {
    if (isLocked && canUseBiometric && !biometricAttempted) {
      setBiometricAttempted(true);
      handleBiometricUnlock();
    }
  }, [isLocked, canUseBiometric, biometricAttempted]);

  // Reset biometric attempted flag when lock state changes
  useEffect(() => {
    if (!isLocked) {
      setBiometricAttempted(false);
    }
  }, [isLocked]);

  // Check for lockout expiry
  useEffect(() => {
    if (lockedUntil) {
      const remaining = lockedUntil - Date.now();
      if (remaining <= 0) {
        setLockedUntil(null);
        setAttempts(0);
        return;
      }

      const timer = setTimeout(() => {
        setLockedUntil(null);
        setAttempts(0);
      }, remaining);

      return () => clearTimeout(timer);
    }
  }, [lockedUntil]);

  const handleBiometricUnlock = useCallback(async () => {
    if (!canUseBiometric) return;

    setIsVerifying(true);
    try {
      const success = await biometricUnlock();
      if (success) {
        unlock();
      }
    } catch (error) {
      console.error('Biometric unlock failed:', error);
    } finally {
      setIsVerifying(false);
    }
  }, [canUseBiometric, biometricUnlock, unlock]);

  const handlePinComplete = useCallback(async (pin: string) => {
    if (lockedUntil && Date.now() < lockedUntil) {
      setPinError('Too many attempts. Please wait.');
      return;
    }

    setPinError(undefined);
    setIsVerifying(true);

    try {
      const isValid = await verifyPin(pin);

      if (isValid) {
        setAttempts(0);
        unlock();
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);

        if (newAttempts >= MAX_PIN_ATTEMPTS) {
          setLockedUntil(Date.now() + LOCKOUT_DURATION_MS);
          setPinError('Too many attempts. Please wait 30 seconds.');
        } else {
          setPinError(`Incorrect PIN. ${MAX_PIN_ATTEMPTS - newAttempts} attempts remaining.`);
        }
      }
    } catch (error) {
      console.error('PIN verification error:', error);
      setPinError('An error occurred. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  }, [verifyPin, unlock, attempts, lockedUntil]);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      unlock();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Failed to sign out');
    }
  }, [unlock, navigate]);

  // Don't render if not locked or no user
  if (!isLocked || !user) {
    return null;
  }

  const isLockedOut = lockedUntil !== null && Date.now() < lockedUntil;
  const remainingSeconds = isLockedOut
    ? Math.ceil((lockedUntil - Date.now()) / 1000)
    : 0;

  return (
    <Dialog open onOpenChange={() => { /* stays until unlock or sign-out */ }}>
      <DialogContent
        data-app-lock-screen=""
        className="inset-0 left-0 top-0 z-toast flex h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 items-center justify-center gap-0 border-0 bg-transparent p-0 shadow-none duration-0 sm:rounded-none data-[state=open]:animate-none data-[state=closed]:animate-none [&>button]:hidden"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {/* DialogContent's overlay is hardcoded bg-black/80. Clear it so the frosted backdrop below still blurs the app. */}
        <style>{`
          body:has([data-app-lock-screen]) [class~="bg-black/80"] {
            background-color: transparent !important;
          }
        `}</style>
        <DialogTitle className="sr-only">Welcome Back</DialogTitle>
        <DialogDescription className="sr-only">
          The app is locked until you unlock or sign out
        </DialogDescription>
        {/* Blurred backdrop - obscures content for security */}
        <div
          className="absolute inset-0 bg-background/80 backdrop-blur-xl"
          aria-hidden="true"
        />

        {/* Modal card */}
        <div className="relative w-full max-w-sm mx-4 p-4 sm:p-6 space-y-4 sm:space-y-4 bg-card border border-border rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="h-6 w-6 sm:h-7 sm:w-7 text-primary" />
            </div>
            <h1 className="text-lg sm:text-xl font-semibold" aria-hidden="true">Welcome Back</h1>
            <p className="text-sm text-muted-foreground truncate">
              {user.email}
            </p>
          </div>

        {/* Biometric Button */}
        {canUseBiometric && (
          <Button
            variant="outline"
            className="w-full h-10 sm:h-12"
            onClick={handleBiometricUnlock}
            disabled={isVerifying}
          >
            <Fingerprint className="h-5 w-5 mr-2" />
            {isVerifying ? 'Verifying...' : 'Use Biometric'}
          </Button>
        )}

        {/* Divider */}
        {canUseBiometric && hasPin && (
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">
                Or enter PIN
              </span>
            </div>
          </div>
        )}

        {/* PIN Input */}
        {hasPin && (
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <KeyRound className="h-4 w-4" />
              <span>Enter your PIN</span>
            </div>

            <PinInput
              onComplete={handlePinComplete}
              error={pinError}
              disabled={isVerifying || isLockedOut}
            />

            {isLockedOut && (
              <p className="text-center text-sm text-muted-foreground">
                Try again in {remainingSeconds} seconds
              </p>
            )}
          </div>
        )}

        {/* No unlock method available */}
        {!canUseBiometric && !hasPin && (
          <div className="text-center text-sm text-muted-foreground">
            <p>No unlock method available.</p>
            <p>Please sign out and log in again.</p>
          </div>
        )}

        {/* Sign Out */}
        <div className="pt-1.5 sm:pt-2 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground hover:text-destructive"
            onClick={handleSignOut}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>
      </DialogContent>
    </Dialog>
  );
}

export default AppLockScreen;
